// Top header + nav

import React from "react";
import { AccountMenu } from "./accountUi.jsx";
import { KS, buildTickerMetrics, resolveTickerMetricConfig } from "./api.jsx";
import { SEED_ITEMS } from "./data.jsx";
import { I } from "./icons.jsx";
import "./header.css";

const GoldSpot = ({ spot }) => {
  const srcLabel = { live: "Live", stale_cache: "Cached", fallback: "Fallback" }[spot.source] || spot.source;
  const age = spot.ageSeconds < 60 ? `${spot.ageSeconds}s` : `${Math.round(spot.ageSeconds/60)}m`;
  return (
    <div className={`spot ${spot.source}`} data-testid="gold-spot" title={`Gold spot - ${srcLabel} - ${age} old - ${spot.isAuthoritative ? "authoritative" : "non-authoritative"}. All valuations depend on this price.`}>
      <span className="spot-ic">Au</span>
      <div style={{display:"flex", flexDirection:"column", lineHeight:1.05}}>
        <span className="spot-val" data-testid="gold-spot-value">${spot.pricePerGram.toFixed(2)}<span className="spot-unit">/g</span></span>
      </div>
      <span className="spot-src" data-testid="gold-spot-source"><span className="sd"/>{srcLabel} - {age}</span>
    </div>
  );
};

const Header = ({
  liveOn,
  active,
  setActive,
  onOpenTweaks,
  theme,
  onThemeChange,
  signedIn,
  onComingSoon,
  spot,
  counts,
  searchTerm,
  onSearch,
  onClearSearch,
  searching,
  brandTagline,
  user,
  onSignIn,
  onSignOut,
  onUpdateProfile,
  onCloseAccount,
}) => {
  const [q, setQ] = React.useState(searchTerm || "");
  React.useEffect(() => { setQ(searchTerm || ""); }, [searchTerm]);
  const submitSearch = (e) => {
    e.preventDefault();
    onSearch?.(q);
  };
  const clear = () => {
    setQ("");
    onClearSearch?.();
  };
  const activeTheme = theme === "dim" ? "dim" : "light";
  const nav = [
    { k: "deals", label: "Deals" },
    { k: "browse", label: "Browse" },
    { k: "alerts", label: "Alerts", gated: true },
    { k: "saved", label: "Saved", gated: true },
    { k: "live", label: "Live", num: liveOn ? "on" : null, toggle: true },
  ];
  return (
    <header className="hdr">
      <div className="hdr-inner">
        <div className="brand">
          <div className="brand-mark">K</div>
          <div>
            <div className="brand-name">Karat<em>Scan</em></div>
            <div className="brand-tag mono">{brandTagline}</div>
          </div>
        </div>

        <div style={{display:"flex", alignItems:"center", gap:"18px", justifyContent:"space-between"}}>
          <nav className="nav">
            {nav.map(n => {
              const isCurrentPage = !n.toggle && active === n.k;
              const isToggleOn = n.toggle && liveOn;
              const className = [
                isCurrentPage ? "active" : "",
                isToggleOn ? "toggle-on" : "",
              ].filter(Boolean).join(" ");
              const ariaLabel = n.gated
                ? `${n.label} (coming soon)`
                : n.toggle
                  ? `${n.label} updates ${liveOn ? "on" : "off"}`
                  : n.label;
              return (
                <button
                  key={n.k}
                  type="button"
                  className={className}
                  data-testid={`nav-${n.k}`}
                  aria-current={isCurrentPage ? "page" : undefined}
                  aria-pressed={n.toggle ? liveOn : undefined}
                  aria-label={ariaLabel}
                  onClick={() => setActive(n.k)}
                  title={n.gated ? "Coming soon" : n.toggle ? `Turn live updates ${liveOn ? "off" : "on"}` : ""}
                >
                  {n.label}{n.num ? <span className="num">{n.num}</span> : null}
                  {n.gated ? <span className="num" style={{opacity:0.6}}>soon</span> : null}
                </button>
              );
            })}
          </nav>
          <form className={`hdr-search ${q || searchTerm ? "has-clear" : ""}`} onSubmit={submitSearch}>
            <I.search size={14} className="s-ico"/>
            <input
              className="input"
              data-testid="search-input"
              placeholder="Search title, item type, seller..."
              value={q}
              onChange={e => setQ(e.target.value)}
              aria-label="Search items"
            />
            {q || searchTerm ? (
              <button className="btn icon sm ghost search-clear" type="button" onClick={clear} title="Clear search"><I.x size={12}/></button>
            ) : (
              <span className="kbd-wrap"><span className="kbd">Enter</span></span>
            )}
          </form>
        </div>

        <div className="hdr-right">
          <GoldSpot spot={spot}/>
          <div className="sep"/>
          <button
            className="btn icon sm ghost hdr-tweaks-btn"
            onClick={onOpenTweaks}
            title="Tweaks"
            aria-hidden="true"
            tabIndex={-1}
          >
            <I.cog size={15}/>
          </button>
          <div
            className="seg hdr-theme-toggle"
            data-testid="header-theme-toggle"
            role="group"
            aria-label="Theme"
          >
            <button
              type="button"
              className={activeTheme === "light" ? "active" : ""}
              aria-label="Light"
              aria-pressed={activeTheme === "light"}
              onClick={() => onThemeChange?.("light")}
            >
              <I.sun size={14}/>
            </button>
            <button
              type="button"
              className={activeTheme === "dim" ? "active" : ""}
              aria-label="Dim"
              aria-pressed={activeTheme === "dim"}
              onClick={() => onThemeChange?.("dim")}
            >
              <I.moon size={14}/>
            </button>
          </div>
          {signedIn ? (
            <>
              <button className="btn icon sm ghost" title="Notifications coming soon" onClick={() => onComingSoon?.("Notifications")}><I.bell size={15}/></button>
              <AccountMenu
                user={user}
                onSignOut={onSignOut}
                onUpdateProfile={onUpdateProfile}
                onCloseAccount={onCloseAccount}
              />
            </>
          ) : (
            <button className="signin-btn" data-testid="signin-button" onClick={() => onSignIn?.()}><I.bolt size={13}/> Sign in</button>
          )}
        </div>
      </div>
    </header>
  );
};

const Ticker = ({ spot, live, tierMetaRevision }) => {
  const config = React.useMemo(
    () => resolveTickerMetricConfig ? resolveTickerMetricConfig() : { refreshMs: 5 * 60 * 1000 },
    []
  );
  const [snapshot, setSnapshot] = React.useState(() => ({
    items: live ? [] : SEED_ITEMS,
    meta: {},
    status: live ? "loading" : "ready",
  }));

  React.useEffect(() => {
    if (!live) {
      setSnapshot({ items: SEED_ITEMS, meta: {}, status: "ready" });
      return undefined;
    }
    if (!KS || !KS.getTickerMetricWindow) {
      setSnapshot((prev) => ({ ...prev, status: "error" }));
      return undefined;
    }

    let alive = true;
    let controller = null;
    const load = () => {
      if (document.hidden) return;
      if (controller && controller.abort) controller.abort();
      controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      KS.getTickerMetricWindow({
        config,
        signal: controller ? controller.signal : undefined,
      })
        .then((next) => {
          if (!alive) return;
          setSnapshot({ ...next, status: "ready" });
        })
        .catch((err) => {
          if (!alive || (err && err.name === "AbortError")) return;
          setSnapshot((prev) => ({ ...prev, status: prev.items.length ? "ready" : "error" }));
        });
    };
    const onVisibility = () => {
      if (!document.hidden) load();
    };

    load();
    const id = setInterval(load, config.refreshMs || 5 * 60 * 1000);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      if (controller && controller.abort) controller.abort();
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [live, config]);

  const metrics = React.useMemo(() => {
    if (!buildTickerMetrics) return [];
    return buildTickerMetrics({
      items: snapshot.items,
      meta: snapshot.meta,
      spot,
      config,
    });
  }, [snapshot, spot, config, tierMetaRevision]);

  const row = (duplicate = false) => (
    <>
      {metrics.map((it) => (
        <span
          key={`${duplicate ? "dup-" : ""}${it.key}`}
          className={`ticker-metric ${it.tone || "neutral"}`}
          data-testid={duplicate ? undefined : `ticker-metric-${it.key}`}
          data-trend={it.trend ? it.trend.direction : undefined}
          data-sample-scope={it.sample && it.sample.scope ? it.sample.scope : undefined}
          data-value-sample-capped={it.sample && it.sample.valueSampleCapped ? "true" : undefined}
          data-trend-sample-capped={it.sample && it.sample.trendSampleCapped ? "true" : undefined}
          data-trend-suppressed={it.trendSuppressed ? "true" : undefined}
          title={it.detail ? `${it.label}: ${it.value} (${it.detail})` : `${it.label}: ${it.value}`}
        >
          <span className="ticker-label">{it.label}</span>
          <span className="ticker-value" data-testid={duplicate ? undefined : "ticker-metric-value"}>{it.value}</span>
          {it.trend ? (
            <span className={`ticker-trend ${it.trend.direction}`} data-testid={duplicate ? undefined : "ticker-metric-trend"}>
              {it.trend.direction === "up" ? "▲" : it.trend.direction === "down" ? "▼" : "·"} {it.trend.label}
            </span>
          ) : null}
          {it.detail ? <span className="ticker-detail" data-testid={duplicate ? undefined : "ticker-metric-detail"}>- {it.detail}</span> : null}
        </span>
      ))}
    </>
  );
  return (
    <div className="ticker-wrap" data-testid="ticker-row" data-status={snapshot.status}>
      <div className="ticker-track">{row(false)}{row(true)}</div>
    </div>
  );
};

export { GoldSpot, Header, Ticker };
